import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button, Card, Notice, PageHeading, SkeletonRows, StatusChip } from '@/components/ui';

type PodSignal = 'ok' | 'lapsed' | 'missing' | 'unknown';
type PodState = 'healthy' | 'degraded' | 'deaf' | 'not_syncing';

interface PodHealth {
  mapping_id: string;
  meeting_space_id: string;
  pod_name: string;
  room_name: string | null;
  state: PodState;
  headline: string;
  provider_subscription: { signal: PodSignal; expires_at: string | null; detail: string };
  zencore_subscription: { signal: PodSignal; detail: string };
  synced_through: string | null;
  last_error_code: string | null;
}

interface HealthSummary {
  total_pods: number;
  healthy: number;
  degraded: number;
  deaf: number;
  not_syncing: number;
  needs_attention: PodHealth[];
  connection_state: { connected: boolean; can_write: boolean; last_discovery_at: string | null };
}

interface SyncEventRow {
  id: string;
  occurred_at: string;
  mapping_id: string | null;
  /** The pod this concerned, resolved server-side. `mapping_id` is a uuid. */
  pod_name: string | null;
  event_type: string;
  outcome: 'ok' | 'skipped' | 'failed';
  /** The MEETING's own times, not the log time. Null when the link is gone. */
  start_time: string | null;
  end_time: string | null;
  detail: Record<string, unknown>;
}

/**
 * Screen 7 — Sync health.
 *
 * The design constraint from the brief: a pod can be healthy one way and deaf
 * the other, and a single dead subscription among 499 healthy ones must be
 * findable WITHOUT SCROLLING. So the pods needing attention come first and on
 * their own — the healthy ones are a count, not a list. Scanning 500 rows for
 * one red dot is not a thing anyone should be asked to do.
 *
 * Both signals are always shown separately. Merging them into one light would
 * hide exactly the case that is hardest to diagnose from the outside: bookings
 * flowing one way and not the other.
 */
export function SyncHealth() {
  const [summary, setSummary] = useState<HealthSummary | null>(null);
  const [log, setLog] = useState<SyncEventRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [health, activity] = await Promise.all([
        api<HealthSummary>('/calendar/health'),
        api<SyncEventRow[]>('/calendar/sync-log?limit=50'),
      ]);
      setSummary(health);
      setLog(activity);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not load sync health.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function reconcile() {
    setBusy(true);
    setNotice(null);
    try {
      await api<{ queued: boolean }>('/calendar/health/reconcile', { method: 'POST', data: {} });
      setNotice(
        'Checking both sides now. Anything we repair will appear in the activity list below within a minute or two.',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not start that check.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] px-[28px] py-[40px]">
      <div className="mb-[28px]">
        <PageHeading
          eyebrow="Calendar"
          title="Sync health"
          sub="Each pod has two connections: one that hears your calendar, one that hears ZenSpace. Both have to work."
          actions={
            <Button tone="quiet" busy={busy} onClick={reconcile}>
              Check both sides now
            </Button>
          }
        />
      </div>

      {notice && (
        <div className="mb-[20px]">
          <Notice tone="healthy" title={notice} />
        </div>
      )}
      {error && (
        <div className="mb-[20px]">
          <Notice tone="broken" title={error} />
        </div>
      )}

      {summary === null ? (
        <SkeletonRows rows={4} label="Loading sync health…" />
      ) : summary.total_pods === 0 ? (
        <Notice tone="neutral" title="No pods are linked yet">
          Link a pod to a room on the Space mapping screen and its health will appear here.
        </Notice>
      ) : (
        <>
          <div className="mb-[28px] grid grid-cols-2 gap-[14px] md:grid-cols-4">
            <Tile label="Syncing both ways" value={summary.healthy} tone="healthy" />
            <Tile label="One way only" value={summary.degraded} tone="attention" />
            <Tile label="Not syncing" value={summary.deaf + summary.not_syncing} tone="broken" />
            <Tile label="Pods linked" value={summary.total_pods} tone="neutral" />
          </div>

          {/* Needing attention FIRST, and alone. */}
          {summary.needs_attention.length === 0 ? (
            <Notice tone="healthy" title={`All ${summary.total_pods} pods are syncing both ways.`} />
          ) : (
            <section className="mb-[40px]">
              <h2 className="mb-[14px] text-subheading font-semibold">
                {summary.needs_attention.length} pod
                {summary.needs_attention.length === 1 ? '' : 's'} need attention
              </h2>
              <ul className="m-0 flex list-none flex-col gap-[14px] p-0">
                {summary.needs_attention.map((pod) => (
                  <li key={pod.mapping_id}>
                    <PodCard pod={pod} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section>
            <h2 className="mb-[14px] text-subheading font-semibold">Recent activity</h2>
            {log.length === 0 ? (
              <Notice tone="neutral" title="Nothing has happened yet">
                Activity appears here as bookings move between your calendar and ZenSpace.
              </Notice>
            ) : (
              <Card padded={false}>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-body">
                    <thead>
                      <tr className="border-b border-divider text-left">
                        <th className="zs-eyebrow px-[14px] py-[10px]">When</th>
                        <th className="zs-eyebrow px-[14px] py-[10px]">Pod</th>
                        <th className="zs-eyebrow px-[14px] py-[10px]">What happened</th>
                        <th className="zs-eyebrow px-[14px] py-[10px]">Meeting</th>
                        <th className="zs-eyebrow px-[14px] py-[10px]">Organiser</th>
                        <th className="zs-eyebrow px-[14px] py-[10px]">Which booking</th>
                        <th className="zs-eyebrow px-[14px] py-[10px]">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {log.map((row) => (
                        <tr key={row.id} className="border-b border-divider last:border-0">
                          <td className="px-[14px] py-[8px] whitespace-nowrap tabular-nums">
                            {new Date(row.occurred_at).toLocaleString()}
                          </td>
                          <td className="px-[14px] py-[8px] whitespace-nowrap">
                            {row.pod_name ?? <span className="text-muted">—</span>}
                          </td>
                          <td className="px-[14px] py-[8px]">
                            {describeEvent(row.event_type)}
                            {trigger(row.detail) && (
                              <span className="block text-caption text-muted">
                                after {trigger(row.detail)}
                              </span>
                            )}
                          </td>
                          <td className="px-[14px] py-[8px] whitespace-nowrap">
                            <Meeting start={row.start_time} end={row.end_time} />
                          </td>
                          <td className="px-[14px] py-[8px]">
                            <Organiser detail={row.detail} />
                          </td>
                          <td className="px-[14px] py-[8px] whitespace-nowrap">
                            <Subject detail={row.detail} />
                          </td>
                          <td className="px-[14px] py-[8px]">
                            <StatusChip
                              tone={
                                row.outcome === 'ok'
                                  ? 'healthy'
                                  : row.outcome === 'skipped'
                                    ? 'neutral'
                                    : 'broken'
                              }
                            >
                              {row.outcome === 'ok'
                                ? 'Done'
                                : row.outcome === 'skipped'
                                  ? 'No action needed'
                                  : 'Failed'}
                            </StatusChip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'healthy' | 'attention' | 'broken' | 'neutral';
}) {
  return (
    <Card>
      <p className="zs-stat-caption m-0">{label}</p>
      <p className="m-0 mt-[4px] text-display leading-none font-semibold text-ink">
        {value}
      </p>
      {value > 0 && tone !== 'neutral' && (
        <div className="mt-[8px]">
          <StatusChip tone={tone}>
            {tone === 'healthy' ? 'Working' : tone === 'attention' ? 'Partly working' : 'Stopped'}
          </StatusChip>
        </div>
      )}
    </Card>
  );
}

function PodCard({ pod }: { pod: PodHealth }) {
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-[14px]">
        <div>
          <p className="m-0 text-subheading font-semibold text-ink">{pod.pod_name}</p>
          <p className="m-0 text-body text-muted">
            {pod.room_name ? `Linked to ${pod.room_name}` : 'Room unknown'}
          </p>
        </div>
        <StatusChip tone={pod.state === 'degraded' ? 'attention' : 'broken'}>
          {pod.headline}
        </StatusChip>
      </div>

      {/* Two signals, never merged. */}
      <dl className="mt-[20px] grid grid-cols-1 gap-[14px] sm:grid-cols-2">
        <Signal
          label="Hearing your calendar"
          signal={pod.provider_subscription.signal}
          detail={pod.provider_subscription.detail}
        />
        <Signal
          label="Hearing ZenSpace"
          signal={pod.zencore_subscription.signal}
          detail={pod.zencore_subscription.detail}
        />
      </dl>

      {pod.synced_through && (
        <p className="mt-[14px] mb-0 text-micro text-muted tabular-nums">
          Checked as far ahead as {new Date(pod.synced_through).toLocaleDateString()}.
        </p>
      )}
    </Card>
  );
}

function Signal({
  label,
  signal,
  detail,
}: {
  label: string;
  signal: PodSignal;
  detail: string;
}) {
  return (
    <div>
      <dt className="zs-eyebrow m-0">{label}</dt>
      <dd className="m-0 mt-[4px]">
        <StatusChip tone={signal === 'ok' ? 'healthy' : signal === 'unknown' ? 'neutral' : 'broken'}>
          {signal === 'ok'
            ? 'Working'
            : signal === 'lapsed'
              ? 'Expired'
              : signal === 'missing'
                ? 'Not set up'
                : 'Unknown'}
        </StatusChip>
        <span className="mt-[4px] block text-body text-muted">{detail}</span>
      </dd>
    </div>
  );
}

/**
 * WHEN THE MEETING ITSELF WAS — not when this line was written.
 *
 * The `When` column is the log time, which answers "what happened at 4:15" and
 * not "which meeting was that". Somebody checking whether the booking they just
 * moved synced needs the meeting's own start and end.
 *
 * The date is dropped from the end time when both fall on one day, which is
 * almost always, so the column reads as a span rather than as two timestamps.
 * Tabular figures, because these sit in a column and are compared.
 */
function Meeting({ start, end }: { start: string | null; end: string | null }) {
  if (!start) return <span className="text-muted">—</span>;

  const from = new Date(start);
  const to = end ? new Date(end) : null;
  const sameDay = to && from.toDateString() === to.toDateString();

  return (
    <span className="flex flex-col gap-[2px] text-caption tabular-nums">
      <span>{from.toLocaleString([], WHEN)}</span>
      {to && (
        <span className="text-muted">
          to {to.toLocaleString([], sameDay ? CLOCK : WHEN)}
        </span>
      )}
    </span>
  );
}

/*
 * No seconds on a meeting. The `When` column keeps them, because that is a log
 * time and seconds are what let a row be matched against a line in the
 * container log. A meeting starts on the minute, and `14:30:00` beside
 * `15:30:00` is two characters of noise in a column meant to be scanned.
 */
const WHEN: Intl.DateTimeFormatOptions = {
  dateStyle: 'short',
  timeStyle: 'short',
};
const CLOCK: Intl.DateTimeFormatOptions = { timeStyle: 'short' };

/**
 * WHOSE MEETING IT WAS.
 *
 * Recorded only where a booking is created — a create, a move's re-make, and a
 * reconciliation repair — because that is where it is known and where somebody
 * checking a mirrored booking asks "who is this?". A dash everywhere else is
 * the honest answer rather than a blank that looks like a bug.
 *
 * The name is shown above the address when there is one, because a row of
 * addresses is far harder to scan than a row of names — but the address is
 * always there, since two people share a name more often than an inbox.
 */
function Organiser({ detail }: { detail: Record<string, unknown> }) {
  const email = asText(detail.organizer_email);
  const name = asText(detail.organizer_name);

  if (!email && !name) return <span className="text-muted">—</span>;

  return (
    <span className="flex flex-col gap-[2px] text-caption">
      {name && <span>{name}</span>}
      {email && <span className="text-muted break-all">{email}</span>}
    </span>
  );
}

/**
 * WHICH BOOKING A ROW IS ABOUT.
 *
 * The sentences alone could not answer "why was MY booking skipped?", because
 * nothing on the row said whose booking it was. Every id needed is already in
 * `detail` and was simply never rendered — so somebody watching a test sync
 * had to read the container logs to match a row to the thing they had just
 * done, which is what this screen exists to avoid.
 *
 * Shortened, because a column of full uuids is unreadable and these are used
 * for recognition rather than for typing. The full value is on the `title`, so
 * it can still be hovered and copied to match against a log line.
 */
function Subject({ detail }: { detail: Record<string, unknown> }) {
  const bookingId = asText(detail.booking_id);
  const eventId = asText(detail.external_event_id);

  if (!bookingId && !eventId) {
    return <span className="text-muted">—</span>;
  }

  return (
    <span className="flex flex-col gap-[2px] text-caption tabular-nums">
      {bookingId && (
        <span title={`ZenSpace booking ${bookingId}`}>
          <span className="text-muted">ZenSpace </span>
          {shorten(bookingId)}
        </span>
      )}
      {eventId && (
        <span title={`Calendar event ${eventId}`} className="text-muted">
          Calendar {shorten(eventId)}
        </span>
      )}
    </span>
  );
}

/**
 * The ZenCore event that caused an outbound write-out.
 *
 * `booking.start` and `booking.end` fire on the clock rather than because
 * anything changed, so most write-outs legitimately end in "no action needed".
 * Without this, a routine clock tick and a real update that did nothing read
 * identically — and only one of them is worth looking into.
 */
function trigger(detail: Record<string, unknown>): string | null {
  return asText(detail.zencore_event);
}

function asText(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Enough of an id to recognise, not enough to fill the column. */
function shorten(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

/** Event types are for us; these sentences are for everyone else. */
const EVENT_WORDS: Record<string, string> = {
  'inbound.booking_created': 'A calendar booking was copied into ZenSpace',
  // A move is a cancel plus a create, so the old booking really is cancelled
  // and a new one really does appear. Said plainly, or the cancellation in
  // ZenSpace looks like something nobody asked for.
  'inbound.booking_moved':
    'A meeting was moved in the calendar — its booking was re-made at the new time',
  'inbound.booking_cancelled': 'A calendar booking was cancelled, and so was the ZenSpace one',
  'inbound.cancellation_skipped_past': 'A cancellation was ignored because the meeting already happened',
  'inbound.sync_completed': 'Checked this pod for calendar changes',
  'inbound.failed': 'A calendar change could not be copied into ZenSpace',
  'outbound.created': 'A ZenSpace booking was added to the calendar',
  'outbound.updated': 'A ZenSpace booking was moved in the calendar',
  'outbound.deleted': 'A booking was cancelled in ZenSpace, so its meeting was removed from the calendar',
  'outbound.skipped_echo': 'No action needed — this booking already exists in the calendar',
  // The most common line in the log by far, and it used to render as its own
  // event code. `booking.start` and `booking.end` fire on the clock rather than
  // because anything changed, so most deliveries legitimately end here.
  'outbound.skipped_unchanged': 'No action needed — the booking had not moved',
  'outbound.skipped_no_booking': 'Nothing to write — the booking or its event was already gone',
  'outbound.failed': 'A ZenSpace booking could not be written to the calendar',
  'outbound.skipped_not_ours': 'Left alone — this meeting was not created by ZenSpace',
  'outbound.released': 'A no-show released the room',
  'release.deleted_our_event':
    'Nobody arrived — the booking was cancelled and our event removed from the calendar',
  'release.declined_their_event':
    'Nobody arrived — the booking was cancelled and the room declined the meeting',
  'reconcile.repaired': 'A missing booking was found and added to ZenSpace',
  'reconcile.repair_failed': 'A missing booking was found, but it could not be added to ZenSpace',
  'reconcile.booking_cancelled': 'A booking was cancelled, because its meeting is no longer in the calendar',
  'release.failed': 'The room could not be freed',
  'release.nothing_to_release': 'Nothing to free — the meeting had already gone',
  'reconcile.divergence': 'A difference was found and reported for someone to look at',
  'reconcile.completed': 'Compared both sides',
  'subscription.renewed': 'Renewed our connection to this room',
  'subscription.renewal_failed': 'Could not renew our connection to this room',
  'sync.bookmark_rejected': 'Re-read this room from scratch, which is routine',
  'sweep.stranded_write': 'A booking that never finished was found',
  'notification.rejected': 'A message arrived that we could not verify, and was ignored',
};

function describeEvent(eventType: string): string {
  return EVENT_WORDS[eventType] ?? eventType.replace(/[._]/g, ' ');
}
